/**
 * Every query key in the application, built here.
 *
 * WHY ONE FILE. A key spelled `["employees", filters]` in the list and `["employee-list",
 * filters]` in the drawer are two caches, and a mutation invalidating one leaves the other
 * showing rows it believes are fresh. Keys are also the invalidation contract: reading this
 * file tells you exactly what a write has to invalidate, which is impossible when keys are
 * inline string literals scattered across forty components.
 *
 * HARD RULE: A SCOPED KEY CARRIES THE COMPANY. `keys.hr.employees.list(filters)` without the
 * legal entity in it would serve company A's employees to a user who has just switched to
 * company B — the request would be correct and the cache would answer first. The company is
 * therefore the FIRST segment after the domain, never buried inside the filter object, so it
 * is visible in devtools and partial invalidation by company works.
 *
 * Self-scoped keys (`mine`) deliberately have no company: "my leave requests" is answered by
 * the server from the caller's own employment, and adding a company would create one cache
 * entry per company for one identical answer.
 */

/**
 * Anything a key can carry as its filter payload.
 *
 * Deliberately structural rather than `Record<string, unknown>`: a DTO-shaped filter object
 * (`LeaveRequestFilters`) has no index signature, and requiring one would force every call
 * site to spread its own filters into a fresh object purely to satisfy the key factory. React
 * Query hashes the value structurally, so an interface is as good a key part as a record.
 */
type Filters = object;

/** A company id, or `null` while the session loads. Null keys are never enabled. */
type Scope = string | null;

export const keys = {
  session: ["session"] as const,

  /**
   * KİŞİSEL İÇERİK DENETİMİ (K4). Her anahtar İNCELEME OTURUMUNU taşıyor, kişiyi değil: aynı
   * kişi için açılan ikinci bir oturum (yeni gerekçe) yeni bir önbellek demek, çünkü her okuma
   * o gerekçeyle kaydediliyor ve eski oturumun cevabını yeniden göstermek, yazılmamış bir
   * okumayı ekrana getirmek olurdu.
   */
  personalContent: {
    people: (filters: Filters) => ["personal-content", "people", filters] as const,
    review: (reviewId: string) => ["personal-content", "review", reviewId] as const,
    conversations: (reviewId: string, filters: Filters) =>
      ["personal-content", "conversations", reviewId, filters] as const,
    messages: (reviewId: string, conversationId: string) =>
      ["personal-content", "messages", reviewId, conversationId] as const,
    notes: (reviewId: string, filters: Filters) =>
      ["personal-content", "notes", reviewId, filters] as const,
    note: (reviewId: string, noteId: string) =>
      ["personal-content", "note", reviewId, noteId] as const,
    noteAttachments: (reviewId: string, noteId: string) =>
      ["personal-content", "note-attachments", reviewId, noteId] as const,
    diagrams: (reviewId: string, filters: Filters) =>
      ["personal-content", "diagrams", reviewId, filters] as const,
    diagram: (reviewId: string, diagramId: string) =>
      ["personal-content", "diagram", reviewId, diagramId] as const,
    diagramExport: (reviewId: string, diagramId: string, format: string) =>
      ["personal-content", "diagram-export", reviewId, diagramId, format] as const,
    documentFolders: (reviewId: string) =>
      ["personal-content", "document-folders", reviewId] as const,
    documents: (reviewId: string, filters: Filters) =>
      ["personal-content", "documents", reviewId, filters] as const,
    documentVersions: (reviewId: string, documentId: string) =>
      ["personal-content", "document-versions", reviewId, documentId] as const,
    jobs: (reviewId: string, filters: Filters) =>
      ["personal-content", "jobs", reviewId, filters] as const,
  },

  hr: {
    leaveRequests: {
      /** The caller's own requests. No company: the server scopes it to their employment. */
      mine: (filters: Filters) => ["hr", "leave-requests", "mine", filters] as const,
      list: (scope: Scope, filters: Filters) =>
        ["hr", "leave-requests", "list", scope, filters] as const,
      detail: (id: string) => ["hr", "leave-requests", "detail", id] as const,
      calendar: (scope: Scope, filters: Filters) =>
        ["hr", "leave-requests", "calendar", scope, filters] as const,
      /** The server's own calculation for a draft — never computed in the browser. */
      preview: (input: Filters) => ["hr", "leave-requests", "preview", input] as const,
    },
    leaveBalances: {
      mine: (filters: Filters) => ["hr", "leave-balances", "mine", filters] as const,
      list: (scope: Scope, filters: Filters) =>
        ["hr", "leave-balances", "list", scope, filters] as const,
      /**
       * Keyed by EMPLOYMENT, not by company — like `mine` and for the same reason. The
       * endpoint requires an employment and answers about that one contract; a company in the
       * key would create one cache entry per company for one identical answer.
       */
      ledger: (employmentId: string | null, filters: Filters) =>
        ["hr", "leave-balances", "ledger", employmentId, filters] as const,
    },
    employeeDocuments: {
      list: (scope: Scope, filters: Filters) =>
        ["hr", "employee-documents", "list", scope, filters] as const,
      types: (scope: Scope) => ["hr", "employee-document-types", scope] as const,
      /** The managed catalogue, keyed by its filters — not the picker's ACTIVE-only entry. */
      typeList: (filters: Filters) =>
        ["hr", "employee-document-types", "list", filters] as const,
    },
    leaveTypes: {
      /**
       * NOT scoped by company: the catalogue belongs to the customer, and the server's list
       * query has no `legalEntityId` to narrow it with. A scope in this key bought one cache
       * entry per company for one identical answer.
       *
       * KİŞİYİ TAŞIYOR (25 Ekim 2026): `forEmploymentId` verilen liste o kişinin cinsiyetine
       * kapalı türleri elemiş hâlde dönüyor. Anahtarda kişi olmasaydı ilk açanın daralmış
       * listesi ötekilere de önbellekten servis edilirdi — birinin eksik, bir başkasının fazla
       * tür görmesi. `null` tam katalog: «İzin Türleri» yönetim ekranının gördüğü.
       */
      all: (forEmploymentId: string | null = null) =>
        ["hr", "leave-types", "all", forEmploymentId] as const,
      list: (filters: Filters) => ["hr", "leave-types", "list", filters] as const,
    },
    leavePolicies: {
      list: (scope: Scope, filters: Filters) =>
        ["hr", "leave-policies", scope, filters] as const,
      detail: (id: string) => ["hr", "leave-policies", "detail", id] as const,
    },
    absenceCalendar: {
      /** Keyed by the window and the narrowing, because that IS the answer. */
      range: (scope: Scope, filters: Filters) =>
        ["hr", "absence-calendar", scope, filters] as const,
    },
    /**
     * ik-yasam-dongusu 19 Ekim 2026 (İK-16): bir şirketin bir yıldaki resmî tatilleri. Şirket ve yıl
     * cevabın kendisi — başka yılın satırları bu anahtarın altında durmasın diye yıl da anahtarda.
     */
    publicHolidays: {
      list: (scope: Scope, year: number) => ["hr", "public-holidays", scope, year] as const,
    },
    employees: {
      list: (scope: Scope, filters: Filters) => ["hr", "employees", "list", scope, filters] as const,
      detail: (id: string) => ["hr", "employees", "detail", id] as const,
      /**
       * Its own key, never merged into the detail key. Sensitive fields are fetched by a
       * separate authorised request and each read is audited on the server; folding them into
       * the detail cache would make an ordinary screen visit look like a deliberate access.
       */
      sensitive: (id: string) => ["hr", "employees", "sensitive", id] as const,
      addresses: (id: string) => ["hr", "employees", "addresses", id] as const,
      contacts: (id: string) => ["hr", "employees", "contacts", id] as const,
      /** Çalışanın bağlı kullanıcı hesabı ve durumu (17 Ekim 2026, #21). */
      account: (id: string) => ["hr", "employees", "account", id] as const,
      /** kisi-bilgi 19 Ekim 2026: kişi kartının «Değişiklik geçmişi» (denetim kaydından). */
      history: (id: string, filters: Filters) => ["hr", "employees", "history", id, filters] as const,
      /** kisi-bilgi 19 Ekim 2026: «Ek bilgiler» — `employee` kayıt türünün özel alan tanımları. */
      customFields: () => ["hr", "employees", "custom-fields"] as const,
    },
    /**
     * kisi-bilgi 19 Ekim 2026: Profil › «Kişisel bilgilerim» — oturumun kişisinin kendi kaydı.
     * Şirketsiz: sunucu kişiyi oturumdan buluyor.
     */
    myDetails: () => ["hr", "my-details"] as const,
    employments: {
      list: (scope: Scope, filters: Filters) =>
        ["hr", "employments", "list", scope, filters] as const,
      detail: (id: string) => ["hr", "employments", "detail", id] as const,
      assignments: (id: string) => ["hr", "employments", "assignments", id] as const,
      /** Çıkış kapatılabilir mi — kararın kendisi, kişi başına. */
      exitReadiness: (id: string) => ["hr", "employments", "exit-readiness", id] as const,
    },
    assets: {
      list: (scope: Scope, filters: Filters) => ["hr", "assets", "list", scope, filters] as const,
      detail: (id: string) => ["hr", "assets", "detail", id] as const,
      /** Kişi başına özet; şirket anahtarın parçası değil, çünkü cevap kişiye ait. */
      summary: (employmentId: string) => ["hr", "assets", "summary", employmentId] as const,
      /** ik-ek 6 Ekim 2026: zimmetin ekleri (fotoğraf, imzalı form) — kayda ait, şirket kaydın kendisinde. */
      attachments: (id: string) => ["hr", "assets", "attachments", id] as const,
    },
    training: {
      /**
       * ŞİRKETE GÖRE ANAHTARLANMIYOR: eğitim İÇERİĞİ kiracı geneli ana veri ve sunucunun
       * liste sorgusunda daraltacak bir `legalEntityId` yok. Anahtara şirket koymak, tek bir
       * aynı cevap için şirket başına bir önbellek satırı üretirdi.
       */
      materials: (filters: Filters) => ["hr", "training", "materials", filters] as const,
      material: (id: string) => ["hr", "training", "materials", "detail", id] as const,
      /** ik-duzeltme 19 Ekim 2026 (E1): kategoriler — kiracı geneli, seçici etkinleri okuyor. */
      categories: (includeInactive: boolean) => ["hr", "training", "categories", includeInactive] as const,
      /** Katılım ŞİRKETE bağlı: kimin aldığı, o kişinin istihdam kaydına dolayısıyla şirkete. */
      completions: (scope: Scope, filters: Filters) =>
        ["hr", "training", "completions", scope, filters] as const,
      coverage: (employmentId: string, category: string) =>
        ["hr", "training", "coverage", employmentId, category] as const,
      /** ik-ek 6 Ekim 2026: içeriğin videoları ve dokümanları (imzalı adresleriyle). */
      attachments: (id: string) => ["hr", "training", "attachments", id] as const,
      /** egitim 21 Ekim 2026: kitle seçicisinin birim/unvan seçenekleri — kiracı geneli. */
      audienceOptions: () => ["hr", "training", "audience-options"] as const,
      /** Seçici açıkken «bu seçimle N kişi»; seçimin kendisi anahtarın parçası. */
      audiencePreview: (audience: Filters) => ["hr", "training", "audience-preview", audience] as const,
      /** Katılım ekranı: eğitime katılması gerekenler. */
      audienceMembers: (id: string) => ["hr", "training", "audience-members", id] as const,
      /**
       * «Eğitimlerim» — çağıranın KENDİ eğitimleri. Şirket yok (`mine` kuralı): sunucu kimliği
       * oturumdan çözüyor.
       */
      mine: () => ["hr", "training", "mine"] as const,
    },
    documents: {
      mine: (filters: Filters) => ["hr", "employee-documents", "mine", filters] as const,
      /**
       * «Zorunlu belgeler» (25 Ekim 2026) — `employee-documents` ailesinde, süzgeçsiz: cevap
       * kişinin kendi beklenen belgeleri ve bir sorgusu yok. Aynı aile, çünkü belge yüklemek
       * onu da tazelemeli; yoksa yüklenen belge listede görünür, «eksik» satırı yerinde kalır.
       */
      checklist: () => ["hr", "employee-documents", "checklist"] as const,
      list: (scope: Scope, filters: Filters) =>
        ["hr", "employee-documents", "list", scope, filters] as const,
      detail: (id: string) => ["hr", "employee-documents", "detail", id] as const,
      /** ik-belge 20 Ekim 2026: dosya sürümleri — `employee-documents` ailesinde, her belge yazması tazeler. */
      versions: (id: string) => ["hr", "employee-documents", "versions", id] as const,
      types: (scope: Scope) => ["hr", "employee-document-types", scope] as const,
    },
    attendance: {
      days: (scope: Scope, filters: Filters) =>
        ["hr", "attendance", "days", scope, filters] as const,
      punches: (filters: Filters) => ["hr", "attendance", "punches", filters] as const,
      /** Bir aralığın toplamı: hem kişi hem pencere cevabın parçası. */
      summary: (employmentId: string, from: string, to: string) =>
        ["hr", "attendance", "summary", employmentId, from, to] as const,
    },
    medicalReports: {
      mine: (filters: Filters) => ["hr", "medical-reports", "mine", filters] as const,
      list: (scope: Scope, filters: Filters) =>
        ["hr", "medical-reports", "list", scope, filters] as const,
      detail: (id: string) => ["hr", "medical-reports", "detail", id] as const,
    },
  },

  workflow: {
    approvals: {
      inbox: (filters: Filters) => ["workflow", "approvals", "inbox", filters] as const,
      detail: (id: string) => ["workflow", "approvals", "detail", id] as const,
      /** The header badge. Separate from the inbox so paging the inbox does not refetch it. */
      count: () => ["workflow", "approvals", "count"] as const,
    },
  },

  notifications: {
    /** Every notification key — what a `notification.created` frame invalidates. */
    all: () => ["notifications"] as const,
    list: (filters: Filters) => ["notifications", "list", filters] as const,
    unreadCount: () => ["notifications", "unread-count"] as const,
    announcements: (filters: Filters) => ["notifications", "announcements", filters] as const,
  },

  /**
   * The personal workspace. None of these keys carries a company.
   *
   * Not an oversight and not the `mine` exception either: these tables have no
   * `legal_entity_id` at all. A note, a conversation, a diagram and a conversion job belong to
   * a USER, and the server narrows every one of those lists by the caller's own id. Adding the
   * active company to the key would fragment one identical answer across every company the
   * person can switch to — and imply a scoping rule the server does not have.
   */
  directory: {
    people: (filters: Filters) => ["directory", "people", filters] as const,
    person: (userId: string) => ["directory", "people", "detail", userId] as const,
    me: () => ["directory", "me"] as const,
  },

  /**
   * KİŞİSEL TAKVİM (28 Ekim 2026; 30 Ekim'de buraya taşındı — anahtarlar Panelim'in bloğunda ve
   * takvim ekranında ayrı ayrı yazılıydı ve aynı ay iki önbellekte duruyordu).
   *
   * `mine` şirket taşımıyor (`mine` kuralı): sunucu kişiyi oturumdan okuyor. Başkasının takvimi
   * (Ekibim'den) İSTİHDAMLA anahtarlanıyor. Aylık plan kaydedilince `mineAll()` tazeleniyor.
   */
  calendar: {
    mineAll: () => ["me", "calendar"] as const,
    /* Ay `{ from, to }` nesnesinde: ay adımında önceki ay ekranda kalsın (`monthStepOnly`). */
    mine: (from: string, to: string) => ["me", "calendar", { from, to }] as const,
    of: (employmentId: string, from: string, to: string) =>
      ["calendar", employmentId, { from, to }] as const,
  },

  /**
   * KİŞİSEL AJANDA (Panelim 30 Ekim 2026). Ekranlar ajandayı takvimden (`calendar.mine`) okuyor;
   * burada yalnız düzenleme çekmecesinin kaydı. Her yazma `calendar.mineAll()`'u da tazeliyor.
   */
  agenda: {
    all: () => ["me", "agenda"] as const,
    detail: (id: string) => ["me", "agenda", id] as const,
  },

  chat: {
    conversations: (filters: Filters) => ["chat", "conversations", filters] as const,
    conversation: (id: string) => ["chat", "conversations", "detail", id] as const,
    /** A cursor list; the filters part carries the page size, never the cursor itself. */
    messages: (conversationId: string, filters: Filters) =>
      ["chat", "messages", conversationId, filters] as const,
    unreadCount: () => ["chat", "unread-count"] as const,
    search: (filters: Filters) => ["chat", "search", filters] as const,
  },

  notes: {
    list: (filters: Filters) => ["notes", "list", filters] as const,
    /**
     * Ray'ın sayfa ağacı (`GET /notes/tree`). `list` ailesinin ALTINDA bilerek: bir not
     * yazmasının bayatlattığı her liste (`["notes","list"]` geçersiz kılması) ağacı da kapsıyor.
     */
    tree: () => ["notes", "list", "tree"] as const,
    detail: (id: string) => ["notes", "detail", id] as const,
    folders: () => ["notes", "folders"] as const,
    tags: () => ["notes", "tags"] as const,
    revisions: (id: string) => ["notes", "revisions", id] as const,
    /** What the server will accept as an attachment — reference data, under the same prefix. */
    attachmentPurposes: () => ["notes", "attachment-purposes"] as const,
  },

  diagrams: {
    list: (filters: Filters) => ["diagrams", "list", filters] as const,
    detail: (id: string) => ["diagrams", "detail", id] as const,
    revisions: (id: string) => ["diagrams", "revisions", id] as const,
    export: (id: string, format: string) => ["diagrams", "export", id, format] as const,
  },

  converter: {
    capabilities: () => ["converter", "capabilities"] as const,
    jobs: (filters: Filters) => ["converter", "jobs", filters] as const,
    job: (id: string) => ["converter", "jobs", "detail", id] as const,
  },

  /**
   * BELGE ŞABLONLARI (`module.templates`) — İKİ AİLE, tek önek altında değil.
   *
   * `templates.*` ŞABLONUN kendisi: müşteri düzeyinde, ortak kalıp, yönetim ekranının konusu.
   * `templateDocuments.*` kişinin o kalıptan doldurduğu BELGE. Ayrı önekler, çünkü bir şablonu
   * yayımlamak kişinin taslaklarını bayatlatmaz (yayımlanmış sürüm donmuştur ve var olan belge
   * kendi sürümüne bağlıdır) — tek önek altında olsalardı her yayın herkesin taslağını yeniden
   * çektirirdi.
   *
   * `available` ŞABLON ailesinde ama ayrı bir yaprak: seçicinin sorusu «hangi kalıptan belge
   * açabilirim», yönetim listesininki «hangi kalıplar var». Bir taslağı kaydetmek ikincisini
   * bayatlatır, birincisini bayatlatmaz — yayın ikisini birden bayatlatır.
   *
   * ANAHTARDA ŞİRKET YOK, ve `documents` ailesindeki gerekçenin aynısı: şablon müşteriye,
   * doldurulan belge KİŞİYE ait. Şirket belgenin bir ALANI (antet ve numara serisi oradan
   * geliyor), önbelleğin bölücüsü değil.
   */
  templates: {
    list: () => ["templates", "list"] as const,
    detail: (id: string) => ["templates", "detail", id] as const,
    versions: (id: string) => ["templates", "versions", id] as const,
    clauses: () => ["templates", "clauses"] as const,
    available: () => ["templates", "available"] as const,
    availableDetail: (id: string) => ["templates", "available", "detail", id] as const,
  },

  templateDocuments: {
    list: (filters: Filters) => ["template-documents", "list", filters] as const,
    detail: (id: string) => ["template-documents", "detail", id] as const,
    /**
     * Şirket seçenekleri ve akıllı alanların ÇÖZÜLMÜŞ değerleri. Şirket anahtarda, çünkü cevap
     * tam olarak ona göre değişiyor: ünvan, adres, vergi dairesi ve belge numarası şirketten
     * geliyor.
     */
    context: (legalEntityId: string | null) =>
      ["template-documents", "context", legalEntityId ?? "varsayilan"] as const,
  },

  documents: {
    session: (id: string) => ["documents", "sessions", id] as const,
    versions: (linkId: string) => ["documents", "versions", linkId] as const,
    /**
     * Kişisel kütüphane. Anahtarda ŞİRKET YOK ve bu bir unutkanlık değil: belge bir KİŞİYE
     * ait, tablo `legal_entity_id` taşımıyor ve sunucu listeyi çağıranın kendi kimliğiyle
     * daraltıyor. Etkin şirketi anahtara koymak, tek ve aynı cevabı kişinin geçebildiği her
     * şirkete parçalardı.
     */
    folders: () => ["documents", "folders"] as const,
    /**
     * Bir klasörün SAYFALI içi (Dosyalarım 13). Sıralama anahtarda: sunucu sıralıyor, ve iki
     * sıralamanın ilk sayfası iki ayrı cevap.
     */
    library: (folderId: string | null, sort: Filters = {}) =>
      ["documents", "library", "list", folderId ?? "kok", sort] as const,
    /** Klasörler arası arama. `["documents", "library"]` önekinde: her yazma onu da bayatlatıyor. */
    librarySearch: (q: string, sort: Filters = {}) =>
      ["documents", "library", "search", q, sort] as const,
    /** Tek belge — ayrıntı çekmecesi, satır o sayfada değilse. */
    document: (id: string) => ["documents", "library", "detail", id] as const,
    trash: () => ["documents", "library", "cop"] as const,
    /** Kurulumun yeteneği: yazmayla değişmiyor, dağıtımla değişiyor. */
    capabilities: () => ["documents", "capabilities"] as const,
    /** Kişisel belge yuvasının türleri ve boyut tavanı (`GET /files/purposes`). */
    uploadPurposes: () => ["files", "purposes", "documents.document"] as const,
    /**
     * «Ne kadar yer kullandım» (K1, `GET /files/usage`) — `["files", …]` önekinde, çünkü
     * ölçtüğü şey Dosyalarım DEĞİL: sohbet ekleri, not ekleri ve Dosyalarım aynı havuzdan
     * sayılıyor. Anahtarı `documents` önekine koymak, sohbette bir ek gönderildiğinde
     * bayatlaması gereken bir cevabı belge yazmalarına bağlamak olurdu.
     */
    storageUsage: () => ["files", "usage"] as const,
    /**
     * ŞİRKETİN ORTAK RAFI — ve BU anahtarlarda şirket VAR, bir üsttekinin tam tersi.
     *
     * Kişisel kütüphanede şirket yoktu çünkü tablo `legal_entity_id` taşımıyor. Raf taşıyor:
     * kişi şirket değiştirdiğinde gördüğü evraklar TAMAMEN değişiyor. Şirketi anahtara
     * koymamak, A şirketinin yönetmeliğini B şirketinde göstermenin kısa yolu olurdu — ve
     * önbellek bunu sessizce, bir istek bile atmadan yapardı.
     */
    companyFolders: (scope: Scope) => ["documents", "company", "folders", scope] as const,
    company: (scope: Scope, folderId: string | null) =>
      ["documents", "company", "list", scope, folderId ?? "kok"] as const,
    companyTrash: (scope: Scope) => ["documents", "company", "trash", scope] as const,
  },

  organization: {
    legalEntities: (filters: Filters) => ["organization", "legal-entities", filters] as const,
    orgUnits: (scope: Scope) => ["organization", "org-units", scope] as const,
    sites: (scope: Scope) => ["organization", "sites", scope] as const,
    jobTitles: (scope: Scope) => ["organization", "job-titles", scope] as const,
    positions: (scope: Scope, filters: Filters) =>
      ["organization", "positions", scope, filters] as const,
    costCenters: (scope: Scope) => ["organization", "cost-centers", scope] as const,
  },

  /**
   * Stok. Every list carries the company: a product catalogue can be shared between two legal
   * entities in one installation, but a warehouse and its balances never are — and a cache
   * that answered before the request would hand company A's stock to somebody who has just
   * switched to company B.
   */
  /**
   * Mali temel. Vergi ve kur, KAPSAMSIZ (`scope` yok): KDV oranı ve döviz kuru şirkete göre
   * değişmez, müşteriye göre değişir ve müşteri anahtarın kendisinde zaten var. Fiyat listesi
   * kapsamlı, çünkü bir liste tek bir şirkete ait olabilir.
   */
  finance: {
    taxCategories: {
      list: (filters: Filters) => ["finance", "tax-categories", "list", filters] as const,
      detail: (id: string) => ["finance", "tax-categories", "detail", id] as const,
      rates: (categoryId: string) => ["finance", "tax-rates", categoryId] as const,
    },
    exchangeRates: (filters: Filters) => ["finance", "exchange-rates", filters] as const,
    priceLists: {
      list: (scope: Scope, filters: Filters) =>
        ["finance", "price-lists", "list", scope, filters] as const,
      detail: (id: string) => ["finance", "price-lists", "detail", id] as const,
      items: (priceListId: string, filters: Filters) =>
        ["finance", "price-lists", "items", priceListId, filters] as const,
    },
    /**
     * Çözülen fiyat, SORUNUN TAMAMIYLA anahtarlanıyor — müşteri, ürün, miktar, tarih. Miktar
     * anahtarın parçası olmasaydı, 10 adet için alınan cevap 200 adet için gösterilirdi ve
     * kademe hiç çalışmamış gibi görünürdü.
     */
    resolvedPrice: (params: Filters) => ["finance", "resolve-price", params] as const,
    partnerPriceLists: (partnerId: string) =>
      ["finance", "partner-price-lists", partnerId] as const,
    /**
     * Yönetim defteri (finans F1, 1 Kasım 2026). Şirket SÜZGECİN içinde (`legalEntityId` ya da «şirketler
     * toplamı» için yok) — kapsam değişince süzgeç değişiyor, anahtar da. Defter yalnız eklemeli ama eşitleme
     * beş dakikada bir yeni satır yazıyor; özet ve liste kısa tazelikte.
     */
    summary: (filters: Filters) => ["finance", "summary", filters] as const,
    ledger: (filters: Filters) => ["finance", "ledger", "list", filters] as const,
    ledgerTotals: (filters: Filters) => ["finance", "ledger", "totals", filters] as const,
    sources: () => ["finance", "sources"] as const,
    categories: () => ["finance", "categories"] as const,
    categoryMaps: () => ["finance", "category-maps"] as const,
    unitCostCenters: (legalEntityId: string | null) => ["finance", "unit-cost-centers", legalEntityId] as const,
    /**
     * Masraf beyanı (finans F2c-1, 2 Kasım 2026). Kişinin kendi listesi ve tekil beyan ayrı anahtarlarda; her yazımdan
     * sonra `expenses.all` altı tazeleniyor (liste, tekil, geçmiş ve beyan edilmemiş kişisel ödemeler birlikte —
     * «Beyana ekle» kalemi listeden düşürüyor). Ekip uçları KAPSAMSIZ anahtarda: kapsamı org şemasından sunucu çözüyor.
     */
    expenses: {
      all: () => ["finance", "expenses"] as const,
      mine: (filters: Filters) => ["finance", "expenses", "mine", filters] as const,
      detail: (id: string) => ["finance", "expenses", "detail", id] as const,
      history: (id: string) => ["finance", "expenses", "history", id] as const,
      options: (legalEntityId: string | null) => ["finance", "expenses", "options", legalEntityId] as const,
      personalPayments: () => ["finance", "expenses", "personal-payments"] as const,
      /** Kapsamdaki bütün beyanlar ve Masraf Kontrolü kuyruğu (finans F2c-2); şirket süzgecin içinde. */
      list: (filters: Filters) => ["finance", "expenses", "list", filters] as const,
    },
    /**
     * Ödenecek, ödeme, ödeme listesi, takvim ve bordro (finans F2c-2, 2 Kasım 2026): bir ödeme yazımı beşini birden
     * değiştiriyor, `payables.all` altı birlikte tazeleniyor. Şirket süzgecin içinde.
     */
    payables: {
      all: () => ["finance", "payables"] as const,
      list: (filters: Filters) => ["finance", "payables", "list", filters] as const,
      detail: (id: string) => ["finance", "payables", "detail", id] as const,
      calendar: (filters: Filters) => ["finance", "payables", "calendar", filters] as const,
      payments: (filters: Filters) => ["finance", "payables", "payments", filters] as const,
      lists: (filters: Filters) => ["finance", "payables", "payment-lists", filters] as const,
      listDetail: (id: string) => ["finance", "payables", "payment-list", id] as const,
      payroll: (filters: Filters) => ["finance", "payables", "payroll", filters] as const,
    },
    /**
     * Genel muhasebe (finans F3, 2 Kasım 2026): şirket süzgecin içinde. Bir fiş kaydı mizanı, ekstreyi, KDV'yi ve gönderim listesini
     * birlikte değiştiriyor: `gl.all` altı tazeleniyor.
     */
    gl: {
      all: () => ["finance", "gl"] as const,
      accounts: (filters: Filters) => ["finance", "gl", "accounts", filters] as const,
      accountOptions: (filters: Filters) => ["finance", "gl", "account-options", filters] as const,
      parties: (filters: Filters) => ["finance", "gl", "parties", filters] as const,
      journal: (filters: Filters) => ["finance", "gl", "journal", filters] as const,
      entry: (id: string) => ["finance", "gl", "entry", id] as const,
      trialBalance: (filters: Filters) => ["finance", "gl", "trial-balance", filters] as const,
      statement: (filters: Filters) => ["finance", "gl", "statement", filters] as const,
      statementSummary: (filters: Filters) => ["finance", "gl", "statement-summary", filters] as const,
      vat: (filters: Filters) => ["finance", "gl", "vat", filters] as const,
      config: (legalEntityId: string | null) => ["finance", "gl", "config", legalEntityId] as const,
      syncEntries: (filters: Filters) => ["finance", "gl", "sync-entries", filters] as const,
      batches: (legalEntityId: string | null) => ["finance", "gl", "batches", legalEntityId] as const,
      syncFiles: () => ["finance", "gl", "sync-files"] as const,
      imports: (filters: Filters) => ["finance", "gl", "imports", filters] as const,
      importFields: (kind: string) => ["finance", "gl", "import-fields", kind] as const,
      exports: (report: string) => ["finance", "gl", "exports", report] as const,
      periods: (filters: Filters) => ["finance", "gl", "periods", filters] as const,
      connectorProviders: () => ["finance", "gl", "connector-providers"] as const,
      connectors: (legalEntityId: string | null) => ["finance", "gl", "connectors", legalEntityId] as const,
      connectorRuns: (id: string, filters: Filters) => ["finance", "gl", "connector-runs", id, filters] as const,
      externalCodes: (filters: Filters) => ["finance", "gl", "external-codes", filters] as const,
      reconSummary: (filters: Filters) => ["finance", "gl", "recon-summary", filters] as const,
      reconItems: (filters: Filters) => ["finance", "gl", "recon-items", filters] as const,
      reconCandidates: (entryId: string) => ["finance", "gl", "recon-candidates", entryId] as const,
      incomeExpense: (filters: Filters) => ["finance", "gl", "income-expense", filters] as const,
    },
    /** Bütçe (finans F5): sürüm, satır, gerçekleşen. */
    budget: {
      all: () => ["finance", "budget"] as const,
      list: (legalEntityId: string | null, year: number | null) => ["finance", "budget", "list", legalEntityId, year] as const,
      detail: (id: string) => ["finance", "budget", "detail", id] as const,
      options: (legalEntityId: string | null) => ["finance", "budget", "options", legalEntityId] as const,
      performance: (filters: Filters) => ["finance", "budget", "performance", filters] as const,
    },
    /** Hekim ağırlama (finans F6, `pharma.value_transfer`). */
    hospitality: {
      all: () => ["finance", "hospitality"] as const,
      line: (lineId: string) => ["finance", "hospitality", "line", lineId] as const,
      physician: (physicianId: string, year: number) => ["finance", "hospitality", "physician", physicianId, year] as const,
    },
    /** Gelen e-fatura (finans F6). */
    invoices: {
      all: () => ["finance", "invoices"] as const,
      list: (filters: Filters) => ["finance", "invoices", "list", filters] as const,
      summary: (legalEntityId: string | null) => ["finance", "invoices", "summary", legalEntityId] as const,
      detail: (id: string) => ["finance", "invoices", "detail", id] as const,
      candidates: (id: string) => ["finance", "invoices", "candidates", id] as const,
    },
    /** Sabit kıymet (finans F6). */
    assets: {
      all: () => ["finance", "assets"] as const,
      list: (filters: Filters) => ["finance", "assets", "list", filters] as const,
      summary: (legalEntityId: string | null) => ["finance", "assets", "summary", legalEntityId] as const,
      detail: (id: string) => ["finance", "assets", "detail", id] as const,
    },
    /** Çalışandan alacak (finans F6). */
    receivables: {
      all: () => ["finance", "receivables"] as const,
      list: (filters: Filters) => ["finance", "receivables", "list", filters] as const,
      summary: (legalEntityId: string | null) => ["finance", "receivables", "summary", legalEntityId] as const,
      detail: (id: string) => ["finance", "receivables", "detail", id] as const,
      mine: () => ["finance", "receivables", "mine"] as const,
      forPayable: (payableId: string | null) => ["finance", "receivables", "for-payable", payableId] as const,
    },
    /** Banka ve kart (finans F6). */
    bank: {
      all: () => ["finance", "bank"] as const,
      accounts: (legalEntityId: string | null, includeInactive: boolean) => ["finance", "bank", "accounts", legalEntityId, includeInactive] as const,
      reconciliation: (accountId: string | null, asOf: string | null) => ["finance", "bank", "reconciliation", accountId, asOf] as const,
      lines: (filters: Filters) => ["finance", "bank", "lines", filters] as const,
      candidates: (lineId: string | null) => ["finance", "bank", "candidates", lineId] as const,
      suggestions: (accountId: string | null) => ["finance", "bank", "suggestions", accountId] as const,
      openRecords: (accountId: string | null, page: number, limit: number) => ["finance", "bank", "open-records", accountId, page, limit] as const,
      statements: (accountId: string | null, page: number, limit: number) => ["finance", "bank", "statements", accountId, page, limit] as const,
      filePreview: (batchId: string | null) => ["finance", "bank", "file-preview", batchId] as const,
      fields: () => ["finance", "bank", "fields"] as const,
    },
    /** Mali raporlar (finans F5). */
    reports: {
      all: () => ["finance", "reports"] as const,
      incomeStatement: (filters: Filters) => ["finance", "reports", "income-statement", filters] as const,
      balanceSheet: (filters: Filters) => ["finance", "reports", "balance-sheet", filters] as const,
      accountCostCenter: (filters: Filters) => ["finance", "reports", "account-cost-center", filters] as const,
      costCenters: (filters: Filters) => ["finance", "reports", "cost-centers", filters] as const,
      people: (filters: Filters) => ["finance", "reports", "people", filters] as const,
      claims: (filters: Filters) => ["finance", "reports", "claims", filters] as const,
    },
    /** Masraf Onay Zinciri (finans F2c-2). */
    approvalFlows: {
      all: () => ["finance", "approval-flows"] as const,
      detail: (key: string) => ["finance", "approval-flows", key] as const,
      people: (term: string) => ["finance", "approval-flows", "people", term] as const,
      peopleByIds: (ids: string) => ["finance", "approval-flows", "people-by-ids", ids] as const,
    },
  },

  /**
   * Satış. Belge ve satırları AYRI anahtarlarda: bir satır eklendiğinde belgenin toplamı
   * değişiyor, yani ikisi birlikte geçersizleşmeli — ama satır listesi sayfalanabilir ve
   * belgenin kendisi değil. Tek anahtar, her satır eklemede belgeyi de yeniden çekerdi.
   */
  /**
   * Pazarlama. Huni AYRI bir anahtarda: bir fırsatın aşaması değiştiğinde hem liste hem huni
   * geçersizleşmeli, ama huni sayfalama taşımıyor ve listeyle aynı anahtarı paylaşsaydı her
   * sayfa değişiminde yeniden hesaplanırdı.
   */
  marketing: {
    leads: {
      list: (filters: Filters) => ["marketing", "leads", "list", filters] as const,
      detail: (id: string) => ["marketing", "leads", "detail", id] as const,
      /** Vergi numarası anahtarın PARÇASI: değişince çakışma kontrolü yeniden çalışmalı. */
      convertPreview: (id: string, taxNumber: string) =>
        ["marketing", "leads", "convert-preview", id, taxNumber] as const,
    },
    opportunities: {
      list: (scope: Scope, filters: Filters) =>
        ["marketing", "opportunities", "list", scope, filters] as const,
      detail: (id: string) => ["marketing", "opportunities", "detail", id] as const,
      pipeline: (scope: Scope, filters: Filters) =>
        ["marketing", "opportunities", "pipeline", scope, filters] as const,
    },
    activities: (filters: Filters) => ["marketing", "activities", filters] as const,
  },

  sales: {
    quotations: {
      list: (scope: Scope, filters: Filters) =>
        ["sales", "quotations", "list", scope, filters] as const,
      detail: (id: string) => ["sales", "quotations", "detail", id] as const,
      lines: (quotationId: string, filters: Filters) =>
        ["sales", "quotations", "lines", quotationId, filters] as const,
    },
    orders: {
      list: (scope: Scope, filters: Filters) =>
        ["sales", "orders", "list", scope, filters] as const,
      detail: (id: string) => ["sales", "orders", "detail", id] as const,
      lines: (orderId: string, filters: Filters) =>
        ["sales", "orders", "lines", orderId, filters] as const,
      /** Ön izleme STOĞA bağlı ve stok her an değişir; kısa ömürlü. */
      preview: (orderId: string) => ["sales", "orders", "preview", orderId] as const,
      // satis-duzeltme 20 Ekim 2026: siparişin sevk belgeleri ve «eksikleri rezerve et» önizlemesi.
      shipments: (orderId: string) => ["sales", "orders", "shipments", orderId] as const,
      reservePreview: (orderId: string) => ["sales", "orders", "reserve-preview", orderId] as const,
    },
    /** Depo: sevk bekleyen siparişler (S-1) — stok belgesi işlenince birlikte tazelenir. */
    fulfilment: {
      list: (scope: Scope, filters: Filters) => ["sales", "fulfilment", "list", scope, filters] as const,
      detail: (id: string) => ["sales", "fulfilment", "detail", id] as const,
    },
  },

  /**
   * Kanal verisi — ecza deposu ve IMS raporları.
   *
   * Şirket anahtarın İLK parçası: raporlar şirket bazında geliyor (İntraline ve LDK ayrı
   * dosyalar) ve şirket değişince liste tamamen değişmeli.
   */
  channel: {
    imports: (scope: Scope, filters: Filters) =>
      ["channel", "imports", scope, filters] as const,
    /** Yükleme önizlemesi: şirket + gövdenin tamamı (dosya, depo, dönem, tür, sıra). */
    preview: (scope: Scope, input: Filters) => ["channel", "preview", scope, input] as const,
    facts: (scope: Scope, filters: Filters) => ["channel", "facts", scope, filters] as const,
    /** Ticari veri (31 Ekim 2026): özet, satış analizi, mutabakat — şirket + dönem (+ süzgeçler). */
    summary: (scope: Scope, filters: Filters) => ["channel", "summary", scope, filters] as const,
    sales: (scope: Scope, filters: Filters) => ["channel", "sales", scope, filters] as const,
    reconciliation: (scope: Scope, filters: Filters) => ["channel", "reconciliation", scope, filters] as const,
    /** Kurallar müşteri geneli (ürün kartı ve iş ortağı gibi): şirket anahtarda yok. */
    issues: (filters: Filters) => ["channel", "issues", filters] as const,
    identity: () => ["channel", "identity"] as const,
    rules: () => ["channel", "rules"] as const,
    distributors: () => ["channel", "distributors"] as const,
    territory: (asOf: string) => ["channel", "territory", asOf] as const,
  },
  inventory: {
    warehouses: (scope: Scope) => ["inventory", "warehouses", scope] as const,
    warehouseList: (scope: Scope, filters: Filters) =>
      ["inventory", "warehouses", "list", scope, filters] as const,
    products: {
      list: (scope: Scope, filters: Filters) =>
        ["inventory", "products", "list", scope, filters] as const,
      detail: (id: string) => ["inventory", "products", "detail", id] as const,
    },
    documents: {
      list: (scope: Scope, filters: Filters) =>
        ["inventory", "documents", "list", scope, filters] as const,
      detail: (id: string) => ["inventory", "documents", "detail", id] as const,
    },
    counts: {
      list: (scope: Scope, filters: Filters) =>
        ["inventory", "counts", "list", scope, filters] as const,
      detail: (id: string) => ["inventory", "counts", "detail", id] as const,
    },
    lots: (scope: Scope, filters: Filters) => ["inventory", "lots", scope, filters] as const,
    balances: (scope: Scope, filters: Filters) =>
      ["inventory", "balances", scope, filters] as const,
    /**
     * The ledger is APPEND-ONLY, so a page that has been fetched can never change — but the
     * SET of pages can grow, and the cursor is part of the answer. Keyed by the filters AND
     * the cursor, because two cursors into the same filter are two different answers.
     */
    ledger: (scope: Scope, filters: Filters) =>
      ["inventory", "ledger", scope, filters] as const,
    reservations: (scope: Scope, filters: Filters) =>
      ["inventory", "reservations", scope, filters] as const,
    reorderRules: (scope: Scope, filters: Filters) =>
      ["inventory", "reorder-rules", scope, filters] as const,
    its: {
      list: (scope: Scope, filters: Filters) =>
        ["inventory", "its-notifications", "list", scope, filters] as const,
      detail: (id: string) => ["inventory", "its-notifications", "detail", id] as const,
    },
    /** A scan is its own answer; the code AND the mode are both part of the question. */
    lookup: (code: string, mode: string) => ["inventory", "lookup", mode, code] as const,
    serialHistory: (serialId: string) => ["inventory", "serials", "history", serialId] as const,
    /** Reference data: changes with a definition, not with a transaction. */
    categories: (scope: Scope) => ["inventory", "product-categories", scope] as const,
    categoryList: (filters: Filters) => ["inventory", "product-categories", "list", filters] as const,
    units: () => ["inventory", "units"] as const,
    /**
     * The MANAGED unit list, separate from the picker's `units()`.
     *
     * A picker wants the active catalogue cached as long as reference data can be; the editor
     * has to show passive rows too and changes with every keystroke in the search box. One
     * entry serving both would either cache the editor's view into every form or refetch every
     * picker on the page each time a filter moved.
     */
    unitList: (filters: Filters) => ["inventory", "units", "list", filters] as const,
    uomConversions: (filters: Filters) => ["inventory", "uom-conversions", filters] as const,
    locations: (scope: Scope, filters: Filters) =>
      ["inventory", "locations", scope, filters] as const,
    barcodes: (filters: Filters) => ["inventory", "product-barcodes", filters] as const,
    serials: (scope: Scope, filters: Filters) => ["inventory", "serials", scope, filters] as const,
    /** Keyed by the product AND the narrowing: availability is a question, not a table. */
    availability: (filters: Filters) => ["inventory", "availability", filters] as const,
  },

  // The administration surfaces. Users and assignments are one family: a revoked role is a
  // change to the user the screen is looking at, so both prefixes invalidate together.
  identity: {
    users: {
      list: (filters: Filters) => ["identity", "users", "list", filters] as const,
      /** «Kullanıcı değiştir»in aday listesi (21 Ekim 2026) — `invalidates.user` kapsar. */
      candidates: (filters: Filters) => ["identity", "users", "candidates", filters] as const,
      detail: (id: string) => ["identity", "users", "detail", id] as const,
      /** Kişinin ikinci faktörü — yönetici gözüyle (17 Ekim 2026, #31). `invalidates.user` kapsar. */
      mfa: (id: string) => ["identity", "users", "mfa", id] as const,
    },
  },
  authorization: {
    roles: {
      list: (filters: Filters) => ["authorization", "roles", "list", filters] as const,
      detail: (id: string) => ["authorization", "roles", "detail", id] as const,
      catalog: () => ["authorization", "roles", "catalog"] as const,
    },
    /** Kısıtlı anahtarlar + çağıranın SYSTEM_ADMIN olup olmadığı (K4). Çağırana bağlı. */
    grantRules: () => ["authorization", "grant-rules"] as const,
    assignments: {
      list: (filters: Filters) => ["authorization", "assignments", "list", filters] as const,
      effective: (userId: string) => ["authorization", "assignments", "effective", userId] as const,
    },
  },
  /**
   * Operasyon. Sevkiyat işinin alt koleksiyonları — duraklar, kalemler, zaman çizelgesi —
   * `job(id)` ÖNEKİNİN ALTINDA, çünkü hiçbiri tek başına değişmiyor: bir durağı sıralamak
   * işin kendisini de değiştirir (durak sayısı ve gecikme işin üstünde duruyor) ve bir olay
   * kaydı zaman çizelgesini de. Tek bir önek geçersizleştirme dördünü birden kapsıyor.
   *
   * Kapı ziyaretleri ve istisnalar işin ALTINDA DEĞİL: ikisi de kendi ekranında, iş seçilmeden
   * okunuyor — kapıdaki görevli hangi işe ait olduğunu henüz bilmiyor, eşleştirme işlemi zaten
   * bunun için var.
   */
  operations: {
    all: ["operations"] as const,
    jobCosts: (jobId: string) => ["operations", "job-costs", jobId] as const,
    vehicles: (scope: Scope, filters: Filters) =>
      ["operations", "vehicles", scope, filters] as const,
    drivers: (scope: Scope, filters: Filters) =>
      ["operations", "drivers", scope, filters] as const,
    /** Kimlik okuması ÖNBELLEKLENMİYOR — anahtar yalnız kaydın izini tutar. */
    driverIdentity: (id: string) => ["operations", "drivers", id, "identity"] as const,
    jobs: (scope: Scope, filters: Filters) => ["operations", "jobs", scope, filters] as const,
    job: (id: string) => ["operations", "jobs", "detail", id] as const,
    jobStops: (id: string) => ["operations", "jobs", "detail", id, "stops"] as const,
    jobItems: (id: string) => ["operations", "jobs", "detail", id, "items"] as const,
    jobTimeline: (id: string) => ["operations", "jobs", "detail", id, "timeline"] as const,
    /** Sevkiyat takibi (1 Kasım 2026): iş satırı + kilometre taşları. */
    tracking: (scope: Scope, filters: Filters) => ["operations", "tracking", scope, filters] as const,
    /** Şoförün kendi seferleri — kapsamsız: kimlik oturumdan. */
    selfTrips: (filters: Filters) => ["operations", "self", "trips", filters] as const,
    selfTrip: (id: string) => ["operations", "self", "trip", id] as const,
    selfTripTimeline: (id: string) => ["operations", "self", "trip", id, "timeline"] as const,
    gateVisits: (scope: Scope, filters: Filters) =>
      ["operations", "gate-visits", scope, filters] as const,
    events: (filters: Filters) => ["operations", "events", filters] as const,
    exceptions: (scope: Scope, filters: Filters) =>
      ["operations", "exceptions", scope, filters] as const,
    deliveries: (scope: Scope, filters: Filters) =>
      ["operations", "deliveries", scope, filters] as const,
    dashboard: (scope: Scope, filters: Filters) =>
      ["operations", "dashboard", scope, filters] as const,
    expiring: (days: number) => ["operations", "compliance", "expiring", days] as const,
  },

  /**
   * Cari kartlar. Alt koleksiyonlar detayın ALTINDA anahtarlanıyor, kendi başlarına değil: bir
   * rol eklemek kartın kendisini de değiştirir (liste rolleri iliştirilmiş gönderiyor), yani
   * `partners.detail(id)` önekini geçersiz kılmak dördünü birden kapsar.
   */
  partners: {
    all: ["partners"] as const,
    list: (filters: unknown) => ["partners", "list", filters] as const,
    detail: (id: string) => ["partners", "detail", id] as const,
    roles: (id: string) => ["partners", "detail", id, "roles"] as const,
    addresses: (id: string) => ["partners", "detail", id, "addresses"] as const,
    contacts: (id: string) => ["partners", "detail", id, "contacts"] as const,
    identifiers: (id: string) => ["partners", "detail", id, "identifiers"] as const,
  },

  configuration: {
    modules: () => ["configuration", "modules"] as const,
    /**
     * UNDER the modules prefix on purpose: switching a module on creates its department's head
     * role, so a module write invalidates this list too and nobody has to remember to add it.
     */
    departments: () => ["configuration", "modules", "departments"] as const,
    /**
     * ÇALIŞMA ALANI AYARLARI (KARARLAR K1 + K2) — ve ALTINDAKİ İKİSİ BU ÖNEKTE, kasten.
     *
     * Üçü tek bir cevabın parçaları: varsayılan kotayı değiştiren bir ayar yazması herkesin
     * kullanım yüzdesini oynatıyor, bir kota grubu yazması etkin kotayı oynatıyor, K2
     * anahtarının çevrilmesi ise ayar listesinin `canManage` alanını değiştiriyor. Önek
     * ortak olduğu için `workspace()` geçersizleştirmesi üçünü birden bayatlatıyor ve
     * «grubu kaydettim ama yüzdeler eski» diye bir ara durum kalmıyor.
     */
    workspace: () => ["configuration", "workspace"] as const,
    quotaGroups: () => ["configuration", "workspace", "quota-groups"] as const,
    storageUsage: () => ["configuration", "workspace", "storage-usage"] as const,
  },

  /**
   * Kurulumun işletimi. ŞİRKET YOK: sunucu, olay kuyruğu ve zamanlanmış işler bütün kurulumun;
   * anahtara şirket koymak, tek ve aynı ölçüm için şirket başına bir önbellek satırı demekti.
   */
  system: {
    /** «Sunucu Durumu» — tek bir anın ölçümü (`measuredAt`), ekran açıkken kendiliğinden yenileniyor. */
    serverStatus: () => ["system", "server-status"] as const,
  },
} as const;

/**
 * The prefixes a write invalidates. Named rather than inlined, so a new screen reading the
 * same data is covered by an existing mutation without anybody remembering to add it.
 */
export const invalidates = {
  /** Any leave write: the caller's list, every company's list, the calendar and balances. */
  leave: [
    ["hr", "leave-requests"],
    ["hr", "leave-balances"],
    ["workflow", "approvals"],
  ],
  /**
   * ik-yasam-dongusu 19 Ekim 2026 (İK-16): bir resmî tatil yazıldı, kaldırıldı ya da yüklendi. Liste
   * kendisi; izin talebinin önizlemesi ve takvimi (`["hr","leave-requests"]` altında) çünkü bir
   * günün dakikası değişti; ekip izin takvimi çünkü tatil günleri oradan da okunuyor.
   */
  publicHoliday: [
    ["hr", "public-holidays"],
    ["hr", "leave-requests"],
    ["hr", "absence-calendar"],
  ],
  employee: [
    ["hr", "employees"],
    ["hr", "employments"],
  ],
  /**
   * BİR KİŞİNİN ADI, İLETİŞİMİ YA DA GİRİŞ E-POSTASI DEĞİŞTİ (kisi-bilgi 19 Ekim 2026, «çokça
   * dinamik»): kişi listesi ve kartı, Kişi Rehberi, sohbetin kişi adları, onay listeleri, kendi
   * Profil'i ve oturumu (Panelim'deki «Merhaba», başlıktaki ad) — adı yazan her yer yeniden okunsun.
   */
  person: [
    ["hr", "employees"],
    ["hr", "employments"],
    ["hr", "my-details"],
    ["directory"],
    ["chat", "conversations"],
    ["workflow", "approvals"],
    ["identity", "users"],
    ["session"],
  ],
  /**
   * A product write. The catalogue itself, plus anything that renders a product's name:
   * balances and documents embed it, so a renamed article must not stay renamed in one list
   * and not the other.
   */
  product: [
    ["inventory", "products"],
    ["inventory", "balances"],
  ],
  /** A warehouse write reaches the pickers that list warehouses, not only the list screen. */
  warehouse: [["inventory", "warehouses"]],
  /**
   * Bir özlük belgesi yazıldı (karar, değiştirme, düzeltme, kaldırma). Belge listeleri + kişinin çıkış
   * hazırlığı (eksik evrak sayımı belgelere bakıyor) + onay kutusu (İK kararı ya da değişiklik, zincirdeki
   * açık onay örneğini kapatabiliyor — ik-belge 20 Ekim 2026).
   */
  document: [
    ["hr", "employee-documents"],
    ["hr", "employments", "exit-readiness"],
    ["workflow", "approvals"],
  ],
  medicalReport: [["hr", "medical-reports"]],
  approval: [
    ["workflow", "approvals"],
    ["hr", "leave-requests"],
  ],
  user: [
    ["identity", "users"],
    ["authorization", "assignments"],
  ],
  role: [
    ["authorization", "roles"],
    ["authorization", "assignments"],
  ],
  module: [["configuration", "modules"]],
  /** A sent, edited or redacted message: the thread, the list that previews it, the badge. */
  chat: [
    ["chat", "conversations"],
    ["chat", "messages"],
    ["chat", "unread-count"],
  ],
  /**
   * Bir sevkiyat işine dokunan her şey. Geçiş, durak, kalem, olay ve istisna aynı işin
   * yüzleri: biri değişince pano sayacı da, kapı listesi de yanlış olur.
   */
  transportJob: [["operations"]],
  note: [["notes"]],
  /** Klasör silmek belgeleri taşıyor, belge silmek klasör sayacını değiştiriyor: ikisi bir. */
  /**
   * `["files", "usage"]` LİSTEDE, çünkü her kütüphane yazması kotayı oynatıyor: yükleme
   * doldurur, silme boşaltır, yeni sürüm ikisini birden yapar (K1: sürümler de sayılıyor).
   * Göstergeyi ayrıca geçersizleştirmek çağıranların işi olsaydı, biri unuttuğunda çubuk
   * eski rakamda kalır ve kullanıcı yer açtığını göremezdi.
   */
  documentLibrary: [["documents", "folders"], ["documents", "library"], ["files", "usage"]],
  diagram: [["diagrams"]],
  converterJob: [["converter", "jobs"]],
  profile: [
    ["directory", "me"],
    ["directory", "people"],
    ["session"],
  ],
} as const;
